import React, { useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  Clock,
  MessageSquare,
  FileText,
  Sparkles,
  Zap,
  Calendar,
  Layers,
  ArrowUpRight,
  ShieldCheck,
  CheckCircle2,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

interface WateenHubAnalyticsProps {
  currentUserDisplayName: string;
}

// Color palette matching Wateen's dark emerald & cyan/indigo aesthetic
const COLORS = ['#10b981', '#6366f1', '#0ea5e9', '#f59e0b', '#ec4899'];

export const WateenHubAnalytics: React.FC<WateenHubAnalyticsProps> = ({
  currentUserDisplayName,
}) => {
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('week');
  const [activeMetricTab, setActiveMetricTab] = useState<'all' | 'messages' | 'time' | 'files'>('all');

  // 1. Hourly/Daily Activity (Messages Sent vs Received)
  const messagesData = {
    today: [
      { name: '08:00', sent: 8, received: 14 },
      { name: '10:00', sent: 24, received: 32 },
      { name: '12:00', sent: 18, received: 22 },
      { name: '14:00', sent: 35, received: 45 },
      { name: '16:00', sent: 42, received: 38 },
      { name: '18:00', sent: 28, received: 30 },
      { name: '20:00', sent: 36, received: 40 },
      { name: '22:00', sent: 19, received: 15 },
    ],
    week: [
      { name: 'السبت', sent: 65, received: 90 },
      { name: 'الأحد', sent: 110, received: 145 },
      { name: 'الإثنين', sent: 135, received: 160 },
      { name: 'الثلاثاء', sent: 142, received: 175 },
      { name: 'الأربعاء', sent: 128, received: 150 },
      { name: 'الخميس', sent: 155, received: 190 },
      { name: 'الجمعة', sent: 75, received: 85 },
    ],
    month: [
      { name: 'الأسبوع 1', sent: 620, received: 780 },
      { name: 'الأسبوع 2', sent: 740, received: 890 },
      { name: 'الأسبوع 3', sent: 810, received: 950 },
      { name: 'الأسبوع 4', sent: 790, received: 920 },
    ],
  };

  // 2. Time Spent in App by Module (Minutes)
  const timeSpentData = {
    today: [
      { category: 'المراسلة', minutes: 75, target: 60 },
      { category: 'وتين AI', minutes: 35, target: 20 },
      { category: 'المكالمات', minutes: 40, target: 30 },
      { category: 'الملفات والخزنة', minutes: 15, target: 15 },
      { category: 'السوق والأعمال', minutes: 20, target: 15 },
    ],
    week: [
      { category: 'المراسلة', minutes: 480, target: 400 },
      { category: 'وتين AI', minutes: 210, target: 150 },
      { category: 'المكالمات', minutes: 180, target: 120 },
      { category: 'الملفات والخزنة', minutes: 95, target: 80 },
      { category: 'السوق والأعمال', minutes: 130, target: 100 },
    ],
    month: [
      { category: 'المراسلة', minutes: 1920, target: 1600 },
      { category: 'وتين AI', minutes: 840, target: 600 },
      { category: 'المكالمات', minutes: 720, target: 500 },
      { category: 'الملفات والخزنة', minutes: 380, target: 320 },
      { category: 'السوق والأعمال', minutes: 520, target: 400 },
    ],
  };

  // 3. File Types Distribution (Pie Chart)
  const filesData = [
    { name: 'صور وفيديو', value: 42, count: '148 ملف' },
    { name: 'مستندات وPDF', value: 28, count: '98 ملف' },
    { name: 'رسائل صوتية', value: 18, count: '63 مقطع' },
    { name: 'روابط ومواقع', value: 8, count: '28 رابط' },
    { name: 'أخرى ومشفرة', value: 4, count: '14 ملف' },
  ];

  // Custom Dark Tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && Array.isArray(payload) && payload.length) {
      return (
        <div className="bg-[#0e1626]/95 border border-slate-700/80 p-3 rounded-xl shadow-2xl backdrop-blur-md text-xs">
          {label ? <p className="font-bold text-slate-200 mb-1.5">{label}</p> : null}
          {payload.map((entry: any, index: number) => {
            const color = entry.color || entry.payload?.fill || COLORS[index % COLORS.length];
            const name = entry.name || entry.dataKey || 'القيمة';
            return (
              <div key={`item-${index}`} className="flex items-center gap-2 text-[11px] py-0.5">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                <span className="text-slate-400">{name}:</span>
                <span className="font-bold text-white font-mono">{entry.value}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-5">
      {/* 1. SECTION HEADER & FILTERS */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#0d1322] border border-slate-800/90 rounded-3xl p-4 sm:p-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-500/20 to-teal-500/30 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
              <TrendingUp className="w-4 h-4" />
            </div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <span>لوحة الإحصائيات والنشاط البصري</span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold">
                تحليلات حية 📊
              </span>
            </h3>
          </div>
          <p className="text-xs text-slate-400">
            تتبع أداء التواصل، فترات الاستخدام اليومية، وتوزيع ملفاتك المشفرة بذكاء ودقة
          </p>
        </div>

        {/* Time range switch */}
        <div className="flex items-center gap-1.5 bg-[#090e18] p-1 rounded-2xl border border-slate-800">
          <button
            onClick={() => {
              setTimeRange('today');
              soundEffects.playPop();
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
              timeRange === 'today'
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            اليوم
          </button>
          <button
            onClick={() => {
              setTimeRange('week');
              soundEffects.playPop();
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
              timeRange === 'week'
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            هذا الأسبوع
          </button>
          <button
            onClick={() => {
              setTimeRange('month');
              soundEffects.playPop();
            }}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
              timeRange === 'month'
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            هذا الشهر
          </button>
        </div>
      </div>

      {/* 2. SUMMARY STAT CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Messages Card */}
        <div className="rounded-2xl bg-[#0d1322] border border-slate-800/90 p-4 hover:border-emerald-500/40 transition group">
          <div className="flex items-center justify-between text-slate-400 text-xs mb-2">
            <span className="flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
              <span>الرسائل المتبادلة</span>
            </span>
            <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded-full border border-emerald-500/20">
              +18.4%
            </span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono mb-1">
            {timeRange === 'today' ? '210' : timeRange === 'week' ? '1,760' : '7,310'}
          </div>
          <p className="text-[11px] text-slate-400">
            {timeRange === 'today' ? '129 مرسلة • 81 مستلمة' : 'معدل نشاط مرتفع وتفاعل مستمر'}
          </p>
        </div>

        {/* Screen Time Card */}
        <div className="rounded-2xl bg-[#0d1322] border border-slate-800/90 p-4 hover:border-indigo-500/40 transition group">
          <div className="flex items-center justify-between text-slate-400 text-xs mb-2">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-indigo-400" />
              <span>الوقت في التطبيق</span>
            </span>
            <span className="text-[10px] text-indigo-400 font-bold bg-indigo-500/10 px-1.5 py-0.5 rounded-full border border-indigo-500/20">
              إنتاجية عالية
            </span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono mb-1">
            {timeRange === 'today' ? '3 س 05 د' : timeRange === 'week' ? '18 س 15 د' : '72 س 40 د'}
          </div>
          <p className="text-[11px] text-slate-400">توفير 45 دقيقة بواسطة مساعد وتين AI</p>
        </div>

        {/* Files Exchanged Card */}
        <div className="rounded-2xl bg-[#0d1322] border border-slate-800/90 p-4 hover:border-sky-500/40 transition group">
          <div className="flex items-center justify-between text-slate-400 text-xs mb-2">
            <span className="flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-sky-400" />
              <span>الملفات المتبادلة</span>
            </span>
            <span className="text-[10px] text-sky-400 font-bold bg-sky-500/10 px-1.5 py-0.5 rounded-full border border-sky-500/20">
              مشفر E2EE
            </span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono mb-1">
            {timeRange === 'today' ? '46' : timeRange === 'week' ? '351' : '1,420'}
          </div>
          <p className="text-[11px] text-slate-400">صور، مستندات PDF وتسجيلات صوتية</p>
        </div>

        {/* AI Efficiency Card */}
        <div className="rounded-2xl bg-[#0d1322] border border-slate-800/90 p-4 hover:border-amber-500/40 transition group">
          <div className="flex items-center justify-between text-slate-400 text-xs mb-2">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>كفاءة وتين AI</span>
            </span>
            <span className="text-[10px] text-amber-400 font-bold bg-amber-500/10 px-1.5 py-0.5 rounded-full border border-amber-500/20">
              +94% دقة
            </span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono mb-1">68 مهمة</div>
          <p className="text-[11px] text-slate-400">صياغة وتلخيص واستخراج مواعيد بالـ AI</p>
        </div>
      </div>

      {/* 3. CHARTS GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* CHART 1: MESSAGES ACTIVITY (AreaChart) - spans 2 columns */}
        <div className="lg:col-span-2 rounded-3xl bg-[#0d1322] border border-slate-800/90 p-4 sm:p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                  <span>حركة الرسائل (المرسلة مقابل المستلمة)</span>
                </h4>
                <p className="text-[11px] text-slate-400">
                  مقارنة التفاعل اليومي ومعدل الاستجابة في المحادثات
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span className="text-slate-300 text-[11px]">مرسلة</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
                  <span className="text-slate-300 text-[11px]">مستلمة</span>
                </div>
              </div>
            </div>

            <div className="w-full h-64 sm:h-72 min-h-[260px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={240}>
                <AreaChart
                  data={messagesData[timeRange]}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorReceived" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#1e293b' }}
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#1e293b' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="sent"
                    name="رسائل مرسلة"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorSent)"
                  />
                  <Area
                    type="monotone"
                    dataKey="received"
                    name="رسائل مستلمة"
                    stroke="#6366f1"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorReceived)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
            <span className="flex items-center gap-1 text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>ذروة النشاط: بين 14:00 و 18:00 مساءً</span>
            </span>
            <span>نسبة الرد السريع: 92%</span>
          </div>
        </div>

        {/* CHART 2: FILE TYPES DISTRIBUTION (PieChart / Donut) */}
        <div className="rounded-3xl bg-[#0d1322] border border-slate-800/90 p-4 sm:p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                  <span>توزيع أنواع الملفات</span>
                </h4>
                <p className="text-[11px] text-slate-400">تصنيف الوسائط والمستندات المشتركة</p>
              </div>
              <span className="text-[10px] text-sky-400 font-mono bg-sky-500/10 px-2 py-0.5 rounded-full border border-sky-500/20">
                100% مشفرة
              </span>
            </div>

            <div className="w-full h-48 sm:h-52 min-h-[200px] relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={190}>
                <PieChart>
                  <Pie
                    data={filesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {filesData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        stroke="#0d1322"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Center Text in Donut */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs text-slate-400 font-medium">الإجمالي</span>
                <span className="text-base font-bold text-white font-mono">351</span>
              </div>
            </div>

            {/* Custom Legend */}
            <div className="space-y-1.5 mt-2">
              {filesData.map((item, idx) => (
                <div key={item.name} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <span className="text-slate-300">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2 font-mono">
                    <span className="text-slate-400">{item.count}</span>
                    <span className="text-white font-bold">{item.value}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CHART 3: TIME SPENT ACROSS WATEEN MODULES (BarChart) */}
      <div className="rounded-3xl bg-[#0d1322] border border-slate-800/90 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div>
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              <span>الوقت المستغرق حسب أقسام وتين (بالدقائق)</span>
            </h4>
            <p className="text-[11px] text-slate-400">
              توزيع استهلاك الوقت بين المراسلة، وتين AI، المكالمات، وإدارة الملفات والأعمال
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2.5 py-1 rounded-xl bg-[#111828] border border-slate-700/80 text-amber-300 text-[11px] font-bold">
              ⏱️ الوقت الفعلي المستغرق
            </span>
          </div>
        </div>

        <div className="w-full h-56 sm:h-64 min-h-[220px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={210}>
            <BarChart
              data={timeSpentData[timeRange]}
              margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="category"
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <YAxis
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="minutes"
                name="دقائق الاستخدام"
                radius={[8, 8, 0, 0]}
                fill="#f59e0b"
              >
                {timeSpentData[timeRange].map((_, index) => (
                  <Cell
                    key={`bar-${index}`}
                    fill={index === 0 ? '#10b981' : index === 1 ? '#6366f1' : index === 2 ? '#0ea5e9' : '#f59e0b'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 pt-3 border-t border-slate-800/80 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="p-2.5 rounded-xl bg-[#090e18] border border-slate-800/70 text-slate-300">
            <div className="text-[10px] text-slate-400 mb-0.5">القسم الأكثر استخداماً:</div>
            <div className="font-bold text-emerald-400">💬 المراسلة الفورية والتفاعل</div>
          </div>
          <div className="p-2.5 rounded-xl bg-[#090e18] border border-slate-800/70 text-slate-300">
            <div className="text-[10px] text-slate-400 mb-0.5">الوقت الموفر بالذكاء الاصطناعي:</div>
            <div className="font-bold text-indigo-400">⚡ 45 دقيقة صياغة واختصار</div>
          </div>
          <div className="p-2.5 rounded-xl bg-[#090e18] border border-slate-800/70 text-slate-300">
            <div className="text-[10px] text-slate-400 mb-0.5">مؤشر التركيز والإنتاجية:</div>
            <div className="font-bold text-amber-400">🌟 ممتاز (9.4 / 10)</div>
          </div>
        </div>
      </div>
    </div>
  );
};
